import summaryListItems from "./resume-summary.json";

/**
 * Professional-summary suggestions, keyed by Area of Expertise.
 *
 * `resume-summary.json` was written before the Area of Expertise dropdown settled
 * on its current wording, so its keys spell things differently: "&" for "and",
 * singular for plural, and a few outright different names. Matching the dropdown
 * value against those keys exactly left 14 of the 30 areas — Marketing and
 * Communications among them — with an empty suggestion panel.
 *
 * This resolves the two spellings instead of rewriting the data file, which the
 * skills and achievements lists key off separately.
 */

type SummaryEntry = Record<string, string[]>;

const ENTRIES = summaryListItems as unknown as SummaryEntry[];

/** Lower-case, "&" spelled out, runs of whitespace collapsed. */
function normalise(key: string): string {
	return key.toLowerCase().replace(/&/g, "and").replace(/\s+/g, " ").trim();
}

const BY_NORMALISED_KEY = new Map<string, string[]>();
for (const entry of ENTRIES) {
	for (const [key, value] of Object.entries(entry)) {
		const alias = normalise(key);
		if (!BY_NORMALISED_KEY.has(alias)) BY_NORMALISED_KEY.set(alias, value);
	}
}

/**
 * Dropdown values whose data lives under a name normalisation alone cannot reach
 * — a plural, or a genuinely different heading.
 */
const EXPLICIT_ALIASES: Record<string, string> = {
	"marketing and communications": "marketing and communication",
	"human resources and recruitment": "human resource",
	"retail and consumer goods": "retail and consumer",
	"transport and logistics": "manufacturing, transport and logistics",
	"advertising, arts and media": "advertising arts and design",
};

/**
 * Suggestions for an Area of Expertise. Returns an empty list when the area has
 * no summaries written for it yet — the caller shows its empty state.
 */
export function summariesForJobFunction(jobFunction?: string | null): string[] {
	const value = normalise((jobFunction ?? "").toString());
	if (!value) return [];

	const aliased = EXPLICIT_ALIASES[value];
	return BY_NORMALISED_KEY.get(value) ?? (aliased ? (BY_NORMALISED_KEY.get(aliased) ?? []) : []);
}

/** Every suggestion in the file, for the unfiltered search. */
export function allSummaries(): string[] {
	return ENTRIES.flatMap((entry) => Object.values(entry).flat());
}
